import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "What services does Lumbini Consultancy offer?",
      answer: "Lumbini Consultancy Services Pvt. Ltd. delivers comprehensive technical and strategic support to NGOs, MSMEs, startups, and public sector entities. Its core offerings span social development (health, education, livelihoods), environmental sustainability, energy efficiency, insurance, HR systems, financial compliance, research, documentation, and skill development. The firm also specializes in consumer goods quality assurance, supply-chain optimization, and export-import facilitation. Lumbini empowers social entrepreneurs by enhancing institutional systems and market strategies. With a focus on integrated, context-driven solutions, it serves clients across India and internationally—bridging operational gaps and driving measurable impact through innovation, advisory, and end-to-end implementation support."
    },
    {
      id: 2,
      question: "How long does it take to complete a project?",
      answer: "Project duration depends on scope and complexity. Standard assignments take 2–6 weeks, while multi-sectoral or field-based projects may extend to 3–6 months. Lumbini ensures timely delivery through detailed planning, milestone tracking, and client consultation—balancing speed with quality to achieve measurable outcomes and maintain transparency throughout the engagement."
    },
    {
      id: 3,
      question: "Do you work with organizations outside of India?",
      answer: "Yes. Lumbini Consultancy Services collaborates with international NGOs, development agencies, startups, and public institutions. Our cross-border engagements include strategic advisory, documentation, compliance, and implementation support—tailored to global standards and local contexts. We ensure seamless coordination and impact-driven solutions for partners operating beyond India."
    },
     {
      id: 4,
      question: "How soon do you onboard Human Resources for recruitment assignments?",
      answer: "Lumbini typically completes staff onboarding within 2 to 4 weeks of assignment confirmation. Our process includes targeted sourcing, rigorous screening, and documentation aligned with client specifications. We ensure timely deployment of qualified professionals through structured planning, sectoral compliance, and transparent coordination across all recruitment engagements."
    },
    {
      id: 5,
      question: "How do you ensure quality in your services?",
      answer: "Lumbini ensures quality through rigorous planning, expert-led execution, and continuous review. Each project follows defined protocols, sectoral benchmarks, and client-approved frameworks. Internal audits, stakeholder feedback, and documentation standards are integral to our process—delivering reliable, impact-driven outcomes with consistency, transparency, and professional excellence across all engagements."
    }
  ];

  const toggleFAQ = (id) => {
    console.log('Toggling FAQ:', id, 'Current open:', openFAQ);
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section className="py-16 sm:py-20 bg-gray-50 relative overflow-hidden">
      {/* Subtle background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(8,145,178,0.03),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(37,99,235,0.02),transparent_50%)]"></div>
      
      {/* Floating elements */}
      <div className="absolute top-16 right-16 w-28 h-28 bg-gradient-to-br from-lumbini-cyan/6 to-transparent rounded-full opacity-35"></div>
      <div className="absolute bottom-16 left-16 w-36 h-36 bg-gradient-to-br from-lumbini-blue/4 to-transparent rounded-full opacity-25"></div>
      <div className="absolute top-1/2 right-1/3 w-12 h-12 bg-gradient-to-br from-accent-400/8 to-transparent rounded-full opacity-30"></div>
      
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
            Frequently Asked <span className="text-lumbini-blue">Questions</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Find answers to common questions about our services and processes.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-400 ease-in-out delay-50"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-lumbini-blue focus:ring-opacity-50 cursor-pointer"
                  type="button"
                  style={{ 
                    zIndex: 10,
                    position: 'relative'
                  }}
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4 flex-1">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    <ChevronDown 
                      className={`h-5 w-5 text-gray-400 transition-all duration-400 ease-in-out ${
                        openFAQ === faq.id 
                          ? 'rotate-180 text-lumbini-blue delay-75' 
                          : 'rotate-0 delay-0'
                      }`} 
                    />
                  </div>
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    openFAQ === faq.id 
                      ? 'max-h-96 opacity-100 border-t border-gray-100 delay-100' 
                      : 'max-h-0 opacity-0 delay-0'
                  }`}
                >
                  <div className="px-6 pb-5">
                    <p className="text-left text-gray-600 leading-relaxed pt-4">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;